import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { 
  navigateToTab as navigateToTabAction, 
  setNavigating 
} from '@/store/slices/navigationSlice';
import { TabManager } from './TabManager';
import { NavigationUtils } from './NavigationUtils';
import { BadgeManager } from './BadgeManager';
import { useTabBadges } from '@/api-frontend/updateTabBadges';

interface MobileNavigationContextType {
  // Tab management
  tabManager: typeof TabManager;
  navigationUtils: typeof NavigationUtils;
  badgeManager: typeof BadgeManager;
  
  // Current state
  currentTabId: string;
  isNavigating: boolean;
  
  // Badge utilities
  refreshBadges: () => void;
  shouldShowNavigation: boolean;
}

const MobileNavigationContext = createContext<MobileNavigationContextType | undefined>(
  undefined
);

interface MobileNavigationProviderProps {
  children: ReactNode;
}

/**
 * Provider for mobile navigation context and utilities
 * Handles automatic tab state synchronization and badge updates
 */
export function MobileNavigationProvider({ children }: MobileNavigationProviderProps) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { isNavigating } = useAppSelector(state => state.navigation);
  const { refreshBadges } = useTabBadges();
  
  // Get current tab ID from location
  const currentTabId = NavigationUtils.parsePathToTab(location.pathname);
  
  // Check if navigation should be shown
  const shouldShowNavigation = NavigationUtils.shouldShowNavigation(location.pathname);

  // Sync Redux state with current location
  useEffect(() => {
    const expectedTabId = NavigationUtils.parsePathToTab(location.pathname);
    
    // Update navigation state to match current location
    dispatch(navigateToTabAction({ 
      tabId: expectedTabId,
      options: { 
        preventHistory: true, 
        direction: 'replace' 
      }
    }));
    
    // Clear navigating state after sync
    setTimeout(() => {
      dispatch(setNavigating(false));
    }, 100);
  }, [location.pathname, dispatch]);

  // Refresh badges when relevant state changes
  useEffect(() => {
    refreshBadges();
  }, [refreshBadges]);

  const contextValue: MobileNavigationContextType = {
    // Utility classes
    tabManager: TabManager,
    navigationUtils: NavigationUtils,
    badgeManager: BadgeManager,
    
    // Current state
    currentTabId,
    isNavigating,
    
    // Methods
    refreshBadges,
    shouldShowNavigation,
  };

  return (
    <MobileNavigationContext.Provider value={contextValue}>
      {children}
    </MobileNavigationContext.Provider>
  );
}

/**
 * Hook to use the mobile navigation context
 */
export function useMobileNavigationContext(): MobileNavigationContextType {
  const context = useContext(MobileNavigationContext);
  
  if (!context) {
    throw new Error(
      'useMobileNavigationContext must be used within a MobileNavigationProvider'
    );
  }
  
  return context;
}

export default MobileNavigationProvider;